/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import React from 'react';
import { observer } from 'mobx-react';
import Button from '@react/react-spectrum/Button';
import DropdownButton from '@react/react-spectrum/DropdownButton';
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger';
import Tooltip from '@react/react-spectrum/Tooltip';
import { MenuItem } from '@react/react-spectrum/Menu';
import HeaderNameComponent from '@ccxi/ccxi-shared-components/src/HeaderNameComponent';
import Header from '@ccxi/ccxi-shared-components/src/Header';
import { FrameSection, FrameItem } from '@ccxi/ccxi-shared-components/src/HeaderFooterBase';
import SubSectionTabs from './subSectionTabs';
import { ANALYTICS } from '../../../../utils/enums';
import { CatalogAnalytics } from '../../../../analytics';
import TranslationUtil from '../../../../utils/translation';
import '../index.scss';

const renderHeaderName = (localizedText) => {
    const props = { headerName: localizedText, className: 'ccapp-h5 catalog-header-name' };

    return (
        <FrameSection key={0} position="left" className="catalog-header-left">
            <FrameItem>
                <HeaderNameComponent {...props} />
            </FrameItem>
        </FrameSection>
    );
};

const renderSubSections = props => (
    <FrameSection key={1} position="middle">
        <FrameItem>
            <SubSectionTabs {...props} />
        </FrameItem>
    </FrameSection>
);

const renderButtons = (appConfig, appsSkeletonModel, actionList, appSection, onAction) => (
    actionList.reduce((buttonList, {
        localizedText, actionId, id, disabled,
    }) => {
        buttonList.push(
            <FrameItem key={id}>
                <Button
                    quiet
                    className="cc-app-header-cta"
                    label={localizedText}
                    disabled={disabled}
                    onClick={() => {
                        CatalogAnalytics.sendAnalytics(appConfig, appsSkeletonModel,
                            ANALYTICS.EVENTS.HEADER_BAR_BUTTON,
                            { actionId, appSection });
                        onAction(actionId, appSection);
                    }} />
            </FrameItem>
        );
        return buttonList;
    }, []));

const renderMenuItems = (appConfig, appsSkeletonModel, actionList, appSection, onAction) => (
    <FrameItem>
        <OverlayTrigger trigger="hover" placement="bottom" delayShow={1000}>
            <DropdownButton>
                {
                    actionList.reduce((menuItemList, {
                        localizedText, actionId, id, disabled,
                    }) => {
                        menuItemList.push(
                            <MenuItem
                                className="header-dropDownMenuItem"
                                key={id}
                                disabled={disabled}
                                onClick={() => {
                                    CatalogAnalytics.sendAnalytics(appConfig, appsSkeletonModel,
                                        ANALYTICS.EVENTS.HEADER_BAR_MENU_ITEM,
                                        { actionId, appSection });
                                    onAction(actionId, appSection);
                                }}>
                                {localizedText}
                            </MenuItem>
                        );
                        return menuItemList;
                    }, [])
                }
            </DropdownButton>
            <Tooltip className="header-dropDownTooltip">
                {TranslationUtil.getMessage('ccx.common.moreactions')}
            </Tooltip>
        </OverlayTrigger>
    </FrameItem>
);


export default observer(({
    appConfig, appsSkeletonModel, allAppsModel, appSection, ...routingProps
}) => {
    const {
        localizedText,
        subSectionAriaLabel,
        actionList = [],
        subSectionList = [],
        onAction,
    } = appSection;
    const content = [renderHeaderName(localizedText)];
    if (subSectionList.length) {
        content.push(renderSubSections({
            appsSkeletonModel,
            allAppsModel,
            appSection,
            subSections: subSectionList,
            subSectionAriaLabel,
            ...routingProps,
        }));
    }
    if (actionList.length) {
        let frameList = [];
        const buttons = actionList.filter(list => list.actionType && list.actionType.content === 'button');
        const menuItems = actionList.filter(list => list.actionType && list.actionType.content === 'menu');
        if (buttons.length) {
            frameList = renderButtons(
                appConfig, appsSkeletonModel, buttons, appSection, onAction
            );
        }
        if (menuItems.length) {
            frameList = [...frameList, renderMenuItems(
                appConfig, appsSkeletonModel, menuItems, appSection, onAction
            )];
        }
        content.push((
            <FrameSection key={2} position="right" className="catalog-header-right">
                {frameList}
            </FrameSection>
        ));
    }
    return (
        <Header className="cc-app-header">
            {content}
        </Header>
    );
});
